import { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { isValid, isSameDay, parseISO } from 'date-fns';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';
import { selectPaisId, selectApplicationId } from 'legacyfrontend/redux/selectors/application-selectors';
import { Snackbar, Alert, Stack } from '@mui/material';
import LoadSpinner from 'legacyfrontend/components/spinner';
import ContactFormModal from '../../components/ContactFormModal';
import PackagePreviewView from './components/package-preview/PackagePreviewView';
import { DEFAULT_TARIFF_NOTE, tariffCurrencyCode, tariffTotalAmount } from './components/package-preview/previewUtils';
import { isExternalSupplierPackage } from './components/package-preview/iconTheme';
import { usePackageCache } from 'legacyfrontend/contexts/PackageCacheContext';
import { Seo } from 'core/components/seo';
import { useAppName } from 'legacyfrontend/hooks/useSafeState';
import store from 'storeClient';
import { DATE_KEYS } from 'legacyfrontend/constants';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { saveSearchParameters, mergeSearchParametersWithStored } from 'legacyfrontend/helpers/searchParametersStorage';
import aplicacionesApi from 'legacyfrontend/api/aplicaciones';
import { useBrandingProps } from 'frontend/features/branding/useBrandingProps';
import 'styles/components/paquete-detalle.css';

const parseSalidasUrl = (searchString) => {
  try {
    const searchParams = new URLSearchParams(
      searchString.startsWith('?') ? searchString : `?${searchString}`
    );
    return {
      id: searchParams.get('id'),
      salidaId: searchParams.get('salidaId'),
    };
  } catch (error) {
    console.error('Error parsing salidas URL:', error);
    return { id: null, salidaId: null };
  }
};

const mapBaseTypeDescriptionToQty = (description) => {
  if (!description || typeof description !== 'string') return null;
  const d = description.trim().toLowerCase();
  if (d.includes('simple') || d.includes('single')) return 1;
  if (d.includes('doble') || d.includes('double')) return 2;
  if (d.includes('triple')) return 3;
  if (d.includes('cuadruple') || d.includes('cuádruple') || d.includes('quadruple')) return 4;
  return null;
};

const InternalPackageDetail = (props) => {
  const history = useHistory();
  const { t } = useTranslation('layout');
  const { fetchPackageSalidas, fetchPackageData } = usePackageCache();
  const [packageData, setPackageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const appName = useAppName();
  const packageInfo = packageData?.paqueteInformacion?.[0] || {};

  const seoData = useMemo(() => {
    const pkgTitle = packageData?.title;
    const destCity = packageData?.paqueteDestinos?.[0]?.city?.name || null;
    const originCity = packageData?.originCity?.name || null;
    const firstPrice = packageInfo?.precioInformacion?.[0];
    const price = firstPrice ? tariffTotalAmount(firstPrice) : undefined;
    const currency = packageData?.currencyAplicacion?.codigo || 'USD';
    const priceStr = price ? getFinalPrice({ currencyCode: currency, total: price }) : '';

    if (!pkgTitle && !destCity) {
      return {
        title: `Detalle del Paquete | ${appName}`,
        description: `Consultá los detalles completos de este paquete turístico en ${appName}.`,
      };
    }

    const title = pkgTitle
      ? `${pkgTitle} | ${appName}`
      : `Paquete a ${destCity} | ${appName}`;

    let description = pkgTitle
      ? `${pkgTitle}. `
      : '';
    if (originCity && destCity) {
      description += `Viajá de ${originCity} a ${destCity}`;
    } else if (destCity) {
      description += `Visitá ${destCity}`;
    }
    if (priceStr) {
      description += ` por ${priceStr}`;
    }
    description += `. Reservá en ${appName} con los mejores precios.`;

    return { title, description };
  }, [packageData, appName]);

  const productJsonLd = useMemo(() => {
    if (!packageData) return null;

    const firstPrice = packageInfo?.precioInformacion?.[0];
    const rawPrice = firstPrice ? tariffTotalAmount(firstPrice) : undefined;
    const currency = packageData?.currencyAplicacion?.codigo || 'USD';
    const imageSrc =
      packageData?.paqueteImagenes?.[0]?.imageUrl ||
      packageData?.paqueteDestinos?.[0]?.city?.imageUrl;

    const ld = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: packageData.title || 'Paquete turístico',
      description: seoData.description,
      ...(imageSrc ? { image: imageSrc } : {}),
    };

    const numericPrice = parseFloat(rawPrice);
    if (rawPrice !== undefined && rawPrice !== null && Number.isFinite(numericPrice)) {
      ld.offers = {
        '@type': 'Offer',
        price: String(numericPrice),
        priceCurrency: currency,
        availability: 'https://schema.org/InStock',
      };
    }

    if (typeof window !== 'undefined') {
      ld.url = window.location.href.split('#')[0];
    }

    return ld;
  }, [packageData, packageInfo, seoData]);
  const [consultaPackage, setConsultaPackage] = useState();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [urlComplete, setUrlComplete] = useState('');
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [userSelectedPrice, setUserSelectedPrice] = useState(null);
  const [availableSalidas, setAvailableSalidas] = useState([]);
  const [loadingSalidas, setLoadingSalidas] = useState(false);
  const [selectedSalidaId, setSelectedSalidaId] = useState(null);
  const [loadingPackageUpdate, setLoadingPackageUpdate] = useState(false);
  const [lastLoadedSalidaId, setLastLoadedSalidaId] = useState(null);
  const [currencyById, setCurrencyById] = useState({});
  
  const fetchingSalidasRef = useRef(false);
  const lastFetchParamsRef = useRef(null);
  const { token } = store.getState().applicationState;
  const paisId = useSelector(selectPaisId);
  const viewingApplicationId = useSelector(selectApplicationId);

  const currencyCode =
    packageData?.currencyAplicacion?.codigo ||
    store.getState().applicationState?.currency?.code ||
    'USD';
  const hotelInfo = packageInfo?.hotelesInformacion?.[0] || {};
  const includedItems = packageData?.paquetesIncluido || [];
  const packageDescription = packageData?.paqueteDescripciones?.[0] || {};
  const packageImages = packageData?.paqueteImagenes || [];

  const { web } = store.getState().applicationState;
  const url = web === 'localhost' ? `http://localhost:${process.env.PORT}` : `https://${web}`;
  const urlIntranet = `${url}/paquetes${props.location.search}`;

  const packageId = useMemo(() => {
    const isNewRoute = props.location.pathname.startsWith('/paquetes');
    if (isNewRoute) {
      const { id } = parseSalidasUrl(props.location.search);
      return id || props.match.params.id;
    }
    return props.match.params.id;
  }, [props.location.pathname, props.location.search, props.match.params.id]);

  const searchParams = useMemo(() => {
    if (!isClient) return new URLSearchParams();
    return new URLSearchParams(props.location.search);
  }, [props.location.search]);

  const providerType = useMemo(() => {
    return searchParams.get('providerType');
  }, [searchParams]);
  const branding = useBrandingProps();
  const agencyBrandColor =
    branding?.heroConfig?.brandColor ||
    branding?.headerConfig?.brandColor ||
    branding?.headerConfig?.iconColor;

  const packageRefNumber = packageInfo?.nroRef || '';

  const departureDate = useMemo(() => {
    return packageInfo?.operationDateSince || null;
  }, [packageInfo]);

  const arrivalDate = useMemo(() => {
    return packageInfo?.operationDateUntil || null;
  }, [packageInfo]);

  const selectedSalida = useMemo(() => {
    if (!selectedSalidaId || !availableSalidas.length) return null;
    return availableSalidas.find(s => s.salidaId === selectedSalidaId);
  }, [selectedSalidaId, availableSalidas]);

  const initialBaseTypeQty = useMemo(() => {
    const desc = packageInfo?.precioInformacion?.[0]?.baseType?.description;
    return mapBaseTypeDescriptionToQty(desc);
  }, [packageInfo]);

  const formattedDates = useMemo(() => {
    if (selectedSalida?.dateSince) {
      const sinceDate = new Date(selectedSalida.dateSince);
      const untilDate = selectedSalida.dateUntil ? new Date(selectedSalida.dateUntil) : sinceDate;

      const sinceUTC = new Date(sinceDate.getUTCFullYear(), sinceDate.getUTCMonth(), sinceDate.getUTCDate());
      const untilUTC = new Date(untilDate.getUTCFullYear(), untilDate.getUTCMonth(), untilDate.getUTCDate());

      const dateSince = formatWithMomentFormat(sinceUTC, DATE_KEYS.FORMAT.DAY_SHORT);
      const dateUntil = formatWithMomentFormat(untilUTC, DATE_KEYS.FORMAT.DAY_SHORT);

      return dateSince === dateUntil ? dateSince : `${dateSince} al ${dateUntil}`;
    }

    if (
      !departureDate ||
      !arrivalDate ||
      !isValid(parseISO(departureDate)) ||
      !isValid(parseISO(arrivalDate))
    ) {
      return '-';
    }

    const depDate = parseISO(departureDate);
    const arrDate = parseISO(arrivalDate);

    return isSameDay(depDate, arrDate)
      ? formatWithMomentFormat(depDate, DATE_KEYS.FORMAT.DAY_SHORT)
      : `${formatWithMomentFormat(depDate, DATE_KEYS.FORMAT.DAY_SHORT)} al ${formatWithMomentFormat(
        arrDate,
        DATE_KEYS.FORMAT.DAY_SHORT
      )}`;
  }, [selectedSalida, departureDate, arrivalDate]);

  const tariffCurrencyIds = (packageInfo?.precioInformacion || [])
    .map((precio) => precio.currencyId)
    .filter(Boolean)
    .join(',');

  useEffect(() => {
    const ids = [...new Set(tariffCurrencyIds.split(',').filter(Boolean))];
    if (!ids.length || !token) return undefined;
    let cancelled = false;
    Promise.all(ids.map((id) => aplicacionesApi.getMoneda(id, token).catch(() => null))).then(
      (currencies) => {
        if (cancelled) return;
        const next = {};
        currencies.forEach((currency) => {
          if (currency?.id && currency?.codigo) next[currency.id] = currency.codigo;
        });
        setCurrencyById(next);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [tariffCurrencyIds, token]);

  useEffect(() => {
    const fetchInternalPackage = async () => {
      try {
        setIsLoading(true);

        const isNewRoute = props.location.pathname.startsWith('/paquetes');
        let packageId = props.match.params.id;
        let salidaId = null;

        if (isNewRoute) {
          const { id, salidaId: extractedSalidaId } = parseSalidasUrl(props.location.search);
          packageId = id || props.match.params.id;
          salidaId = extractedSalidaId;
        }

        let data;
        try {
          data = await fetchPackageData(packageId, token, salidaId);
        } catch (salidasError) {
          console.warn('Error con endpoint de salidas, fallback a endpoint:', salidasError);
          data = await fetchPackageData(packageId, token);
        }

        setPackageData(data);

        if (salidaId) {
          const parsedSalidaId = parseInt(salidaId);
          setSelectedSalidaId(parsedSalidaId);
          setLastLoadedSalidaId(parsedSalidaId);
        }

        setError(null);
      } catch (err) {
        setError(err);
        history.replace('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInternalPackage();
  }, [props.match.params.id, props.location.pathname, props.location.search, fetchPackageData, token]);

  useEffect(() => {
    if (packageData && isClient) {
      const mergedParams = mergeSearchParametersWithStored(
        props.location.search,
        packageData,
        selectedSalidaId,
        true
      );
      saveSearchParameters(mergedParams);
    }
  }, [packageData, selectedSalidaId, props.location.search]);

  const monthYearFromUrl = useMemo(() => {
    const monthYear = searchParams.get('monthYear');
    if (!monthYear) {
      const now = new Date();
      return `${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}`;
    }
    return monthYear;
  }, [searchParams]);

  const adultsQty = useMemo(() => {
    const qty = searchParams.get('adultsQty');
    return qty ? parseInt(qty, 10) : null;
  }, [searchParams]);

  useEffect(() => {
    const fetchAvailableSalidas = async () => {
      if (!packageId || !token) return;

      const userSelectedQty = userSelectedPrice ? mapBaseTypeDescriptionToQty(userSelectedPrice.baseType?.description) : null;
      const effectiveAdultsQty = userSelectedQty ?? adultsQty ?? mapBaseTypeDescriptionToQty(selectedPrice?.baseType?.description) ?? initialBaseTypeQty;

      const fetchKey = `${packageId}-${monthYearFromUrl}-${effectiveAdultsQty}`;
      
      if (fetchingSalidasRef.current || lastFetchParamsRef.current === fetchKey) {
        return;
      }

      try {
        fetchingSalidasRef.current = true;
        lastFetchParamsRef.current = fetchKey;
        setLoadingSalidas(true);

        const salidas = await fetchPackageSalidas(packageId, monthYearFromUrl, token, effectiveAdultsQty);

        if (!salidas || salidas.length === 0) {
          setAvailableSalidas([]);
          return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let filteredSalidas = salidas.filter((salida) => {
          if (!salida.dateSince) return false;

          const salidaDate = new Date(salida.dateSince);
          const salidaDateUTC = new Date(
            Date.UTC(
              salidaDate.getUTCFullYear(),
              salidaDate.getUTCMonth(),
              salidaDate.getUTCDate()
            )
          );
          
          const todayUTC = new Date(
            Date.UTC(
              today.getUTCFullYear(),
              today.getUTCMonth(),
              today.getUTCDate()
            )
          );

          return salidaDateUTC >= todayUTC;
        });

        const [month, year] = monthYearFromUrl.split('-').map(Number);

        filteredSalidas = filteredSalidas.map((salida) => {
          if (!salida.dateSince) return salida;

          const salidaDate = new Date(salida.dateSince);
          const salidaMonth = salidaDate.getUTCMonth();
          const salidaYear = salidaDate.getUTCFullYear();

          if (salidaMonth < month - 1 || (salidaMonth === month - 2 && salidaYear < year)) {
            const adjustedDate = new Date(Date.UTC(year, month - 1, 1));
            return {
              ...salida,
              dateSince: adjustedDate.toISOString(),
            };
          }

          return salida;
        });

        setAvailableSalidas(filteredSalidas);

        if (!selectedSalidaId && filteredSalidas.length > 0) {
          setSelectedSalidaId(filteredSalidas[0].salidaId);
        }
      } catch (error) {
        console.error('[InternalPackageDetail] Error al obtener las salidas disponibles:', error);
        setAvailableSalidas([]);
      } finally {
        setLoadingSalidas(false);
        fetchingSalidasRef.current = false;
      }
    };

    if (packageId) {
      fetchAvailableSalidas();
    }
  }, [packageId, token, monthYearFromUrl, selectedPrice, initialBaseTypeQty, adultsQty, userSelectedPrice]);

  useEffect(() => {
    const updatePackageDataWithSalida = async () => {
      if (!selectedSalidaId || !packageId || !token) return;

      const isNewRoute = props.location.pathname.startsWith('/paquetes');
      if (!isNewRoute) return;

      if (lastLoadedSalidaId === selectedSalidaId) return;

      try {
        setLoadingPackageUpdate(true);

        const data = await fetchPackageData(packageId, token, selectedSalidaId);
        setPackageData(data);
        setLastLoadedSalidaId(selectedSalidaId);

        if (isClient && window.history) {
          const urlParams = new URLSearchParams();
          urlParams.set('id', packageId);
          urlParams.set('salidaId', selectedSalidaId);
          if (monthYearFromUrl) {
            urlParams.set('monthYear', monthYearFromUrl);
          }
          if (adultsQty) {
            urlParams.set('adultsQty', adultsQty.toString());
          }
          if (providerType) {
            urlParams.set('providerType', providerType);
          }
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.pushState({ salidaId: selectedSalidaId }, '', newUrl);
        }
      } catch (error) {
        console.error('Error al actualizar el paquete con la nueva salida:', error);
      } finally {
        setLoadingPackageUpdate(false);
      }
    };

    updatePackageDataWithSalida();
  }, [selectedSalidaId, packageId, token, props.location.pathname, lastLoadedSalidaId, fetchPackageData, monthYearFromUrl, adultsQty, providerType]);


  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentUrl = isClient ? window.location.href : '';
      const isNewRoute = currentUrl.includes('/paquetes');

      let packageId = props.match.params.id;
      let salidaId = null;

      if (isNewRoute) {
        const { id, salidaId: extractedSalidaId } = parseSalidasUrl(props.location.search);
        packageId = id || props.match.params.id;
        salidaId = extractedSalidaId;
      }

      const baseUrl = isClient ? window.location.origin : '';
      const packshareParams = new URLSearchParams();
      if (salidaId) {
        packshareParams.set('salidaId', salidaId);
      }
      if (monthYearFromUrl) {
        packshareParams.set('monthYear', monthYearFromUrl);
      }
      if (adultsQty) {
        packshareParams.set('adultsQty', adultsQty.toString());
      }
      if (providerType) {
        packshareParams.set('providerType', providerType);
      }
      const packshareUrl = `${baseUrl}/packshare/${packageId}${packshareParams.toString() ? `?${packshareParams.toString()}` : ''}`;

      setUrlComplete(packshareUrl);
    }
  }, [props.match.params.id, props.location.pathname, props.location.search, monthYearFromUrl, adultsQty, providerType]);

  useEffect(() => {
    if (packageInfo?.precioInformacion?.length > 0) {
      let priceToSelect = packageInfo.precioInformacion[0];

      if (adultsQty && adultsQty > 0) {
        const matchingPrice = packageInfo.precioInformacion.find((price) => {
          const priceQty = mapBaseTypeDescriptionToQty(price.baseType?.description);
          return priceQty === adultsQty;
        });
        
        if (matchingPrice) {
          priceToSelect = matchingPrice;
        }
      }

      setSelectedPrice(priceToSelect);
    }
  }, [packageInfo, adultsQty]);

  useEffect(() => {
    isClient && window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    if (!selectedPrice || !isClient || !window.history) return;

    const derivedAdultsQty = mapBaseTypeDescriptionToQty(selectedPrice?.baseType?.description);
    
    if (derivedAdultsQty && derivedAdultsQty > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const currentAdultsQty = urlParams.get('adultsQty');
      
      if (currentAdultsQty !== derivedAdultsQty.toString()) {
        urlParams.set('adultsQty', derivedAdultsQty.toString());
        
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({ adultsQty: derivedAdultsQty }, '', newUrl);
      }
    }
  }, [selectedPrice]);

  const apiNote = String(selectedPrice?.note || '').trim();
  const priceNote = apiNote || (paisId === 1 ? DEFAULT_TARIFF_NOTE(currencyCode) : null);

  if (isLoading) {
    return (
      <>
        <Seo {...seoData} jsonLd={productJsonLd} />
        <Stack
          sx={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
            alignItems: 'center',
          }}
        >
          <LoadSpinner />
        </Stack>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Seo {...seoData} jsonLd={productJsonLd} />
        <Stack
          sx={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
            alignItems: 'center',
          }}
        >
          <p>Error loading package details</p>
        </Stack>
      </>
    );
  }

  return (
    <>
      <Seo {...seoData} jsonLd={productJsonLd} />
      <PackagePreviewView
        title={packageData?.title || ''}
        images={packageImages}
        shareUrl={urlIntranet}
        descriptionHtml={packageDescription?.description}
        packageRefNumber={packageRefNumber}
        onCopy={() => setShowSnackbar(true)}
        destinations={packageData?.paqueteDestinos}
        originName={packageData?.originCity?.name}
        formattedDates={formattedDates}
        totalNights={packageData?.totalNights}
        includedItems={includedItems}
        selectedPrice={selectedPrice}
        currencyCode={currencyCode}
        currencyById={currencyById}
        priceNote={priceNote}
        availableSalidas={availableSalidas}
        selectedSalidaId={selectedSalidaId}
        onSelectSalida={setSelectedSalidaId}
        loadingSalidas={loadingSalidas}
        tariffs={packageInfo?.precioInformacion}
        onSelectTariff={(precio) => {
          setSelectedPrice(precio);
          setUserSelectedPrice(precio);
        }}
        loadingPackageUpdate={loadingPackageUpdate}
        itinerary={packageData?.itinerarioDetalles}
        includedHtml={packageDescription?.included}
        notIncludedHtml={packageDescription?.notIncluded}
        observationsHtml={packageDescription?.observations}
        trips={packageInfo?.trips}
        hotels={packageInfo?.hotelesInformacion}
        packageUrlWhatsapp={packageInfo?.urlWhatsapp}
        temaIconos={packageData?.temaIconos}
        colorTemaIconos={packageData?.colorTemaIconos}
        ordenSecciones={packageData?.ordenSecciones}
        isSupplierPackage={isExternalSupplierPackage(
          packageData,
          viewingApplicationId,
          providerType,
        )}
        agencyBrandColor={agencyBrandColor}
        onEmailClick={() => {
          setConsultaPackage({
            ...packageData,
            title:
              packageData?.title || packageData?.paqueteInformacion?.[0]?.title || 'Paquete',
            id: packageData?.id || props.match.params.id,
            externalId: packageData?.externalId || packageData?.id || props.match.params.id,
            isInternal: true,
            originCity: {
              name: packageData?.originCity?.name || 'Ciudad no especificada',
            },
            paqueteDestinos: packageData?.paqueteDestinos || [],
            hotelInformation: [
              {
                ...hotelInfo,
                roomType: hotelInfo?.roomType || 'STANDARD',
                occupation: hotelInfo?.occupation || 1,
              },
            ],
            total: selectedPrice
              ? getFinalPrice({
                  currencyCode: tariffCurrencyCode(selectedPrice, currencyCode, currencyById),
                  total: tariffTotalAmount(selectedPrice),
                })
              : `${currencyCode} 0`,
            providerName:
              packageData?.provider?.nombre ||
              packageData?.provider?.nombreFantasia ||
              'Internal',
            link: urlIntranet,
          });
        }}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        open={showSnackbar}
      >
        <Alert onClose={() => setShowSnackbar(false)} severity='success' sx={{ width: '100%' }}>
          {t('layout:common.link_copied')}
        </Alert>
      </Snackbar>
      <ContactFormModal
        consultaPackage={consultaPackage}
        onClose={() => setConsultaPackage(undefined)}
        variant='package'
      />
    </>
  );
};

export default InternalPackageDetail;
